import React, { useEffect, useState, useRef } from "react";
import { useDebouncedCallback } from "use-debounce";
import { DEBOUNCE_DELAY } from "../../../features/tests/utils/constants";

interface DebouncedRadioProps {
  id: string;
  name: string;
  value: string;
  checked: boolean;
  onChange: (value: string) => void;
  className?: string;
  disabled?: boolean;
  required?: boolean;
}

/**
 * A reusable radio button component that debounces changes to avoid excessive re-renders
 */
export const DebouncedRadio: React.FC<DebouncedRadioProps> = ({
  id,
  name,
  value,
  checked: initialChecked,
  onChange,
  className = "h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-gray-600",
  disabled = false,
  required = false,
}) => {
  // Local state for immediate UI updates
  const [localChecked, setLocalChecked] = useState<boolean>(initialChecked);
  
  // Reference to track if the update is coming from local change or prop change
  const isLocalChange = useRef(false);

  // Update local value when prop changes, but only if it's not a local change
  useEffect(() => {
    if (!isLocalChange.current) {
      setLocalChecked(initialChecked);
    }
    // Reset the flag after each render
    isLocalChange.current = false;
  }, [initialChecked]);

  // Debounced callback for onChange
  const debouncedOnChange = useDebouncedCallback((value: string) => {
    // This will trigger a prop change, but we want to ignore it
    // since we already updated the local state
    isLocalChange.current = true;
    onChange(value);
  }, DEBOUNCE_DELAY);

  // Handle radio change
  const handleChange = () => {
    setLocalChecked(true);
    debouncedOnChange(value);
  };

  return (
    <input
      type="radio"
      id={id}
      name={name}
      checked={localChecked}
      onChange={handleChange}
      className={className}
      disabled={disabled}
      required={required}
    />
  );
};
